import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'framer-motion';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';
import * as Styled from 'theme/components/Dropdown.style';
import useClickOutside from 'shared/hooks/useClickOutside';
import useDropdownDirection from 'shared/hooks/useDropdownDirection';
import useListboxKeyboardNavigation from 'shared/hooks/useListboxKeyboardNavigation';

export type DropdownMenuItem = {
  label: React.ReactNode;
  subContent?: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
  icon?: React.ReactNode;
  leadingIcon?: React.ReactNode;
  primary?: boolean;
  selected?: boolean;
  destructive?: boolean;
  onSelect: (ctx: { close: () => void }) => void;
};

export type DropdownMenuFooterAction = {
  label: React.ReactNode;
  icon?: React.ReactNode;
  onClick: () => void;
};

export type DropdownMenuTriggerRenderProps = {
  isOpen: boolean;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  open: (e: React.SyntheticEvent<HTMLElement>) => void;
  close: () => void;
  toggle: (e: React.SyntheticEvent<HTMLElement>) => void;
};

interface DropdownMenuProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  items: DropdownMenuItem[];
  renderTrigger: (props: DropdownMenuTriggerRenderProps) => React.ReactNode;
  menuWidth?: string;
  headerLabel?: React.ReactNode;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  footerAction?: DropdownMenuFooterAction;
  usePortal?: boolean;
}

export function DropdownMenu({
  isOpen,
  onOpenChange,
  items,
  renderTrigger,
  menuWidth,
  headerLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  footerAction,
  usePortal = false,
}: DropdownMenuProps): JSX.Element {
  const { t } = useTranslation('shared');
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hasSearch = !!searchPlaceholder;
  const hasStructuredLayout = hasSearch || !!footerAction;
  const { openUp } = useDropdownDirection({
    open: isOpen,
    triggerRef: rootRef,
    listRef,
  });
  const [portalPos, setPortalPos] = useState<{
    top?: number;
    bottom?: number;
    right: number;
  } | null>(null);

  const close = useCallback(() => {
    if (hasSearch) onSearchChange?.('');
    onOpenChange(false);
  }, [hasSearch, onSearchChange, onOpenChange]);
  const open = useMemo(() => () => onOpenChange(true), [onOpenChange]);
  const toggle = useMemo(
    () => () => {
      if (isOpen && hasSearch) onSearchChange?.('');
      onOpenChange(!isOpen);
    },
    [isOpen, hasSearch, onSearchChange, onOpenChange]
  );

  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const closeRef = useRef(close);
  closeRef.current = close;

  const setOpen: React.Dispatch<React.SetStateAction<boolean>> = useCallback(
    (next) => {
      const resolved =
        typeof next === 'function' ? next(isOpenRef.current) : next;
      if (resolved) {
        onOpenChange(true);
      } else {
        closeRef.current();
      }
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (isOpen && hasSearch) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isOpen, hasSearch]);

  useEffect(() => {
    if (!isOpen || !usePortal) return undefined;

    const updatePosition = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const GAP = 10;
      const EDGE_PADDING = 16;
      const panelWidth = listRef.current?.offsetWidth || 260;
      const desiredRight = window.innerWidth - rect.right;
      const maxRight = Math.max(
        0,
        window.innerWidth - panelWidth - EDGE_PADDING
      );
      const right = Math.max(0, Math.min(desiredRight, maxRight));
      setPortalPos(
        openUp
          ? { bottom: window.innerHeight - rect.top + GAP, right }
          : { top: rect.bottom + GAP, right }
      );
    };

    updatePosition();
    const raf = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, usePortal, openUp]);

  const [interactable, setInteractable] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setInteractable(true), 300);
      return () => clearTimeout(timer);
    }
    setInteractable(false);
    return undefined;
  }, [isOpen]);

  useClickOutside(usePortal ? [rootRef, listRef] : [rootRef], close, isOpen);

  const { createOptionKeyDownHandler } = useListboxKeyboardNavigation({
    open: isOpen,
    setOpen,
    triggerRef,
    listRef,
    selectedOptionSelector: `[data-selection-option="true"][data-selected="true"]`,
  });

  const renderItems = () =>
    items.map((item, index) => {
      const hasSubContent = !!item.subContent;

      const handleSelect = () => {
        if (!item.disabled) item.onSelect({ close });
      };

      const button = (
        <Styled.ItemButton
          key={index}
          role="menuitem"
          disabled={item.disabled}
          tabIndex={item.disabled ? -1 : 0}
          $primary={item.primary}
          $selected={item.selected}
          $destructive={item.destructive}
          $hasSubContent={hasSubContent}
          data-selection-option="true"
          data-selected={item.selected ? 'true' : 'false'}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            handleSelect();
          }}
          onKeyDown={createOptionKeyDownHandler(handleSelect)}
          initial={{ opacity: 0, y: -10 }}
          animate={{
            opacity: item.disabled ? 0.5 : 1,
            y: 0,
            transition: {
              delay: index * 0.03,
              duration: 0.2,
              ease: 'easeOut',
            },
          }}
          exit={{
            opacity: 0,
            y: -10,
            transition: { duration: 0.15, ease: 'easeIn' },
          }}
        >
          {item.leadingIcon && (
            <Styled.ItemLeadingIcon>{item.leadingIcon}</Styled.ItemLeadingIcon>
          )}
          {hasSubContent ? (
            <Styled.ItemContent>
              {item.label}
              <Styled.ItemSubContent>{item.subContent}</Styled.ItemSubContent>
            </Styled.ItemContent>
          ) : (
            item.label
          )}
          {item.icon && <Styled.ItemIcon>{item.icon}</Styled.ItemIcon>}
        </Styled.ItemButton>
      );

      if (item.disabled && item.disabledReason) {
        return (
          <Tooltip
            key={index}
            title={item.disabledReason}
            arrow
            placement="left"
          >
            <span>{button}</span>
          </Tooltip>
        );
      }

      return button;
    });

  const triggerProps: DropdownMenuTriggerRenderProps = {
    isOpen,
    triggerRef,
    open: (e) => {
      e.stopPropagation();
      open();
    },
    close,
    toggle: (e) => {
      e.stopPropagation();
      toggle();
    },
  };

  const panelContent = (
    <AnimatePresence>
      {isOpen && (
        <Styled.Panel
          key="menu"
          role="menu"
          ref={listRef}
          $width={menuWidth}
          $hasSearch={hasStructuredLayout}
          $openUp={openUp}
          style={{
            ...(usePortal && portalPos
              ? {
                  position: 'fixed' as const,
                  top: portalPos.top,
                  bottom: portalPos.bottom,
                  right: portalPos.right,
                }
              : undefined),
            pointerEvents: interactable ? undefined : 'none',
          }}
          initial={{ scale: 0.15, opacity: 0 }}
          animate={{
            scale: 1,
            opacity: 1,
            transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] },
          }}
          exit={{
            scale: 0.15,
            opacity: 0,
            transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
          }}
        >
          {headerLabel && (
            <Styled.SectionHeader>{headerLabel}</Styled.SectionHeader>
          )}
          {hasSearch && (
            <Styled.SearchHeader>
              <Styled.SearchInputWrapper>
                <Styled.SearchInputIcon>
                  <General.SearchSm size={20} />
                </Styled.SearchInputIcon>
                <Styled.SearchInput
                  ref={searchInputRef}
                  placeholder={searchPlaceholder}
                  value={searchValue ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onSearchChange?.(e.target.value)
                  }
                />
              </Styled.SearchInputWrapper>
            </Styled.SearchHeader>
          )}
          {hasStructuredLayout ? (
            <Styled.ItemsList>
              {items.length > 0 ? (
                renderItems()
              ) : (
                <Styled.NoResults>{t('dropdown.noResults')}</Styled.NoResults>
              )}
            </Styled.ItemsList>
          ) : (
            renderItems()
          )}
          {footerAction && (
            <Styled.FooterWrapper>
              <Styled.FooterButton
                type="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  footerAction.onClick();
                }}
              >
                {footerAction.icon}
                {footerAction.label}
              </Styled.FooterButton>
            </Styled.FooterWrapper>
          )}
        </Styled.Panel>
      )}
    </AnimatePresence>
  );

  return (
    <Styled.Root ref={rootRef}>
      {renderTrigger(triggerProps)}
      {usePortal ? createPortal(panelContent, document.body) : panelContent}
    </Styled.Root>
  );
}
